import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import { useAuthStore } from '../stores/authStore';
import type { Business, BusinessHour, BusinessRules } from '../types';

interface TeamCounts {
  total: number;
  employees: number;
  contractors: number;
  operators: number;
}

interface UseSettingsResult {
  business: Business | null;
  businessHours: BusinessHour[];
  teamCounts: TeamCounts;
  serviceCount: number;
  loading: boolean;
  error: string | null;
  saved: boolean;
  updateBusiness: (updates: Partial<Business>) => Promise<boolean>;
  updateBusinessProfile: (updates: Partial<Business>) => Promise<boolean>;
  updateBusinessHours: (hours: BusinessHour[]) => Promise<void>;
  updateBusinessRules: (rules: Partial<BusinessRules>) => Promise<boolean>;
  refresh: () => void;
}

const DEFAULT_HOURS: BusinessHour[] = [
  { day_of_week: 1, is_open: true, open_time: '09:00', close_time: '17:00' },
  { day_of_week: 2, is_open: true, open_time: '09:00', close_time: '17:00' },
  { day_of_week: 3, is_open: true, open_time: '09:00', close_time: '17:00' },
  { day_of_week: 4, is_open: true, open_time: '09:00', close_time: '17:00' },
  { day_of_week: 5, is_open: true, open_time: '09:00', close_time: '17:00' },
  { day_of_week: 6, is_open: false, open_time: '09:00', close_time: '17:00' },
  { day_of_week: 0, is_open: false, open_time: '09:00', close_time: '17:00' },
];

export function useSettings(businessId: string | undefined): UseSettingsResult {
  const [business, setBusiness] = useState<Business | null>(null);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [teamCounts, setTeamCounts] = useState<TeamCounts>({ total: 0, employees: 0, contractors: 0, operators: 0 });
  const [serviceCount, setServiceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const showSaved = useCallback(() => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [bizRes, hoursRes, usersRes, servicesRes] = await Promise.all([
        supabase
          .from('businesses')
          .select('id, name, slug, industry, address, city, province, postal_code, phone, email, timezone, tier, modules_enabled, business_rules, vocabulary, country, region_code, ot_warning_threshold, require_gps_checkin, checkin_radius_m, require_ot_approval, payroll_addon, stripe_customer_id, stripe_subscription_id, trial_ends_at, lifetime_people_count, created_at, stripe_connect_account_id, stripe_connect_onboarding_complete, stripe_connect_charges_enabled, stripe_connect_payouts_enabled, stripe_connect_onboarded_at, client_language, auto_translate_messages, owner_id')
          .eq('id', businessId)
          .maybeSingle(),
        supabase
          .from('business_hours')
          .select('id, day_of_week, is_open, open_time, close_time')
          .eq('business_id', businessId)
          .order('day_of_week', { ascending: true }),
        supabase
          .from('users')
          .select('id, employment_type')
          .eq('business_id', businessId)
          .eq('status', 'active'),
        supabase
          .from('services')
          .select('id')
          .eq('business_id', businessId)
          .eq('is_active', true),
      ]);

      setBusiness(bizRes.data as Business | null);

      const hours = (hoursRes.data ?? []) as BusinessHour[];
      setBusinessHours(hours.length >= 7 ? hours : DEFAULT_HOURS);

      const users = usersRes.data ?? [];
      setTeamCounts({
        total: users.length,
        employees: users.filter(u => u.employment_type === 'employee').length,
        contractors: users.filter(u => u.employment_type === 'contractor').length,
        operators: users.filter(u => u.employment_type === 'operator').length,
      });
      setServiceCount((servicesRes.data ?? []).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['businesses'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  const updateBusiness = useCallback(async (updates: Partial<Business>): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: err } = await supabase.from('businesses').update(updates).eq('id', businessId);
      if (err) throw err;
      // §Settings.UpdateBusinessActivityLogActor: logged_by is the ACTOR column on
      // activity_log (BEP §10.12) — the acting public user (authStore.user resolves
      // through get_public_user_id at sign-in; not persisted by design), never the
      // business id.
      const actorId = useAuthStore.getState().user?.id ?? null;
      try { await supabase.from('activity_log').insert({ business_id: businessId, activity_type: 'settings_updated', description: `Business settings updated: ${Object.keys(updates).join(', ')}`, entity_type: 'business', entity_id: businessId, logged_by: actorId }); } catch { /* non-critical */ }
      showSaved();
      await fetchAll();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      return false;
    }
  }, [businessId, fetchAll, showSaved]);

  const updateBusinessHours = useCallback(async (hours: BusinessHour[]) => {
    if (!businessId) return;
    try {
      // Delete existing then insert all 7
      await supabase.from('business_hours').delete().eq('business_id', businessId);
      const rows = hours.map(h => ({
        business_id: businessId,
        day_of_week: h.day_of_week,
        is_open: h.is_open,
        open_time: h.open_time,
        close_time: h.close_time,
      }));
      const { error: err } = await supabase.from('business_hours').insert(rows);
      if (err) throw err;
      // A deliberate admin save confirms the hours (Today Hub Spec v1 X13) —
      // mirrors the booking_page_customized_at stamp in useBookingPageSettings.
      const { error: stampErr } = await supabase
        .from('businesses')
        .update({ business_hours_customized_at: new Date().toISOString() })
        .eq('id', businessId);
      if (stampErr) throw stampErr;
      showSaved();
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hours');
    }
  }, [businessId, fetchAll, showSaved]);

  const updateBusinessProfile = useCallback(async (updates: Partial<Business>): Promise<boolean> => {
    if (!businessId || !business) return false;
    try {
      const { data, error: rpcErr } = await supabase.rpc('update_business_profile', {
        p_business_id: businessId,
        p_name:        updates.name        ?? business.name,
        p_phone:       updates.phone        ?? business.phone        ?? null,
        p_email:       updates.email        ?? business.email        ?? null,
        p_address:     updates.address      ?? business.address      ?? null,
        p_city:        updates.city         ?? business.city         ?? null,
        p_province:    updates.province     ?? business.province     ?? null,
        p_postal_code: updates.postal_code  ?? business.postal_code  ?? null,
        p_country:     updates.country      ?? business.country      ?? null,
        p_timezone:    updates.timezone     ?? business.timezone,
        p_latitude:    updates.latitude     ?? business.latitude     ?? null,
        p_longitude:   updates.longitude    ?? business.longitude    ?? null,
      });
      if (rpcErr) throw rpcErr;
      if (!data?.success) throw new Error(data?.error?.code ?? 'Save failed');
      showSaved();
      await fetchAll();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      return false;
    }
  }, [businessId, business, fetchAll, showSaved]);

  const updateBusinessRules = useCallback(async (rules: Partial<BusinessRules>): Promise<boolean> => {
    if (!businessId || !business) return false;
    try {
      const merged = { ...(business.business_rules ?? {}), ...rules };
      const { error: err } = await supabase.from('businesses').update({ business_rules: merged }).eq('id', businessId);
      if (err) throw err;
      showSaved();
      await fetchAll();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rules');
      return false;
    }
  }, [businessId, business, fetchAll, showSaved]);

  return { business, businessHours, teamCounts, serviceCount, loading, error, saved, updateBusiness, updateBusinessProfile, updateBusinessHours, updateBusinessRules, refresh: fetchAll };
}
